// core
import React, { useEffect, useState, useContext } from "react";

// components
import Loader from "../../common/components/Loader/Loader";

// providers
import { QuoteGroupContext } from "../providers/RootContextProvider";

// API
import { getGroupAPI, createGroupAPI } from "../api/api";

// interfaces
import { IGroupResult } from "../interfaces/IGroupResult";
import { useHistory, useLocation } from "react-router-dom";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { SplitTreatments } from "@splitsoftware/splitio-react";
import { FeatureFlag } from "../enums/FeatureFlags";
import { isClientProductType } from "../../common/interfaces/enums/ProductType";
import { getProductTypeAsString } from "../../common/shared/enumHelpers";

const SessionContainer: React.FC<{}> = (props) => {
    const location = useLocation();
    const history = useHistory();
    const ErrorLogger = useErrorLogger();
    const search = new URLSearchParams(location.search);

    const [isLoading, setLoading] = useState(false);

    const { actions, selectors } = useContext(QuoteGroupContext);

    const hasGroupIdParam = search.has("groupId");

    const hasStartOverParam = search.has("startover");

    const groupId = selectors.getGroupId();

    const handleSelectionState = (quoteCases: IGroupResult["quoteGroupCases"]): void => {
        quoteCases.forEach((item) => {
            if (isClientProductType(item.config.productType))
                actions.setProductEnabled(item.config.productType, item.isSelected);
        });
    };

    const handleGroup = async (res: IGroupResult): Promise<void> => {
        if (res.groupId) {
            if (hasGroupIdParam) {
                // We need to 'reset' the state a little, just in case.
                // So an old group Id doesn't cross over into the new group.
                actions.clearQuoteResults();
            }

            actions.setGroupId(res.groupId);

            actions.setHasContactDetails(res.hasContactDetails);

            const activeClients = res.clients.filter((i) => i.isActive);
            actions.setClients(activeClients);
            actions.populateQuoteConfigs(res.quoteGroupCases.map((s) => s.config));

            handleSelectionState(res.quoteGroupCases);

            search.delete("groupId");
            history.replace(`${location.pathname}?${search.toString()}`);
        }
    };

    const retrieveGroup = async (): Promise<IGroupResult> => {
        let retrieveGroupId = groupId;

        if (hasGroupIdParam) {
            retrieveGroupId = search.get("groupId") || "";
        } else if (hasStartOverParam) {
            return createGroupAPI();
        }

        return getGroupAPI(retrieveGroupId).catch(() => {
            return createGroupAPI();
        });
    };

    useEffect(() => {
        if (!isLoading) {
            setLoading(true);
            retrieveGroup()
                .then((groupRes) => handleGroup(groupRes))
                .catch((err) => {
                    ErrorLogger.Log(err, {
                        source: "Session Container - Create"
                    });
                })
                .finally(() => setLoading(false));
        }
    }, [groupId]);

    useEffect(() => {
        actions.setDigitalAdvice(search.get("da"));
    }, [search.has("da")]);

    return (
        <SplitTreatments names={Object.values(FeatureFlag)}>
            {({ isReady }) => {
                /* Pre-load all the treatments */
                if (window.splitioFactory && (!isReady || isLoading || !groupId))
                    return <Loader size="full-page" />;
                return <React.Fragment>{props.children}</React.Fragment>;
            }}
        </SplitTreatments>
    );
};

export default SessionContainer;
